import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { Slot } from '@radix-ui/react-slot';
import type { Header } from '@tanstack/react-table';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { useCallback, useContext, useId, useRef, useState } from 'react';
import { cn } from '../../styles/lib/utils';
import { Button, IconButton } from '../Button';
import { ColumnMenu, type ColumnMenuProps } from '../ColumnMenu';
import { DragHandle } from '../Drag';
import { GridContext, type GridSpacing } from '../Grid';
import { GridCellText } from '../GridCellText';
import { Icon } from '../Icon';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { Separator } from '../Separator';
import { SkeletonLoader } from '../SkeletonLoader';
import { SvgIcon } from '../SvgIcon';
import { Tooltip } from '../Tooltip';
import { useCombinedDragAndSort } from './helper';

const gridHeadVariants = cva(
  'group flex items-center gap-0.5 border-b border-grey-30 pl-2 pr-0 py-1 flex-shrink-0 max-w-none',
  {
    variants: {
      spacing: {
        cozy: 'h-8',
        comfy: 'h-10',
        roomy: 'h-13',
      },
    },
    defaultVariants: {
      spacing: 'cozy',
    },
  },
);

const labelButtonVariants = cva('', {
  variants: {
    spacing: {
      cozy: 'h-6',
      comfy: 'h-8',
      roomy: 'h-11',
    },
  },
  defaultVariants: {
    spacing: 'cozy',
  },
});

const menuButtonVariants = cva(
  '!px-0 !py-0.5 rounded-sm bg-white active:bg-blue-10 active:border active:border-blue-50',
  {
    variants: {
      variant: {
        hollow: 'border border-transparent',
        selected: 'border border-blue-50 bg-blue-10 hover:bg-blue-10',
      },
      spacing: {
        cozy: 'h-6',
        comfy: 'h-8',
        roomy: 'h-11',
      },
    },
    defaultVariants: {
      spacing: 'cozy',
    },
  },
);

type GridHeadTooltipIcon = 'infoCircled' | 'questionMarkCircled';

type GridHeadProps = React.ComponentProps<'th'> &
  VariantProps<typeof gridHeadVariants> &
  Omit<ColumnMenuProps, 'className' | 'closePopover' | 'columnLabel'> & {
    asChild?: boolean;
    label?: string;
    sortLabel?: string;
    'data-testid'?: string;
    isLoading?: boolean;
    canResize?: boolean;
    canReorder?: boolean;
    fixedPinning?: boolean;
    filter?: boolean;
    menu?: boolean;
    isDragging?: boolean;
    header?: Header<any, unknown>;
    dragHandleProps?: Record<string, any>;
    showDropIndicator?: boolean;
    tooltip?: React.ReactNode;
    tooltipIcon?: GridHeadTooltipIcon;
  };

const GridHead = React.forwardRef<HTMLTableCellElement, GridHeadProps>(
  (
    {
      className,
      spacing,
      label,
      sortLabel,
      'data-testid': dataTestId,
      isLoading = false,
      canResize = true,
      canReorder = false,
      fixedPinning = false,
      filter = false,
      asChild = false,
      menu = true,
      header,
      isDragging,
      dragHandleProps,
      showDropIndicator = false,
      tooltip,
      tooltipIcon = 'infoCircled',
      // ColumnMenu props
      canHide = false,
      canPin = false,
      canSort = false,
      canFilter = false,
      showColumn,
      onShowColumnChange,
      pinPosition,
      onPinPositionChange,
      sortDirection,
      onSortDirectionChange,
      canMoveLeft,
      canMoveRight,
      onMove,
      canNarrow,
      canWiden,
      canResetWidth,
      onResize,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const headRef = useRef<HTMLTableCellElement>(null);
    const composedRef = useComposedRefs(ref, headRef);
    const menuTriggerRef = useRef<HTMLButtonElement>(null);
    const sortStateId = useId();
    const filterId = useId();

    const Comp = asChild ? Slot : 'th';
    const gridContext = useContext(GridContext);
    const effectiveSpacing = spacing || gridContext.spacing || 'cozy';

    const sortDir = sortDirection && sortDirection !== 'none' ? sortDirection : false;
    const hasIcon = sortDir || filter;
    const ariaSort = canSort && sortDir ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined;
    const menuLabel = label ? `${label} column menu` : 'Column menu';

    const isResizing = header?.column.getIsResizing() ?? false;

    // Handle sort toggle: none -> asc -> desc -> none
    const handleSortToggle = useCallback(() => {
      if (!onSortDirectionChange) return;

      if (!sortDirection || sortDirection === 'none') {
        onSortDirectionChange('asc');
      } else if (sortDirection === 'asc') {
        onSortDirectionChange('desc');
      } else {
        onSortDirectionChange('none');
      }
    }, [onSortDirectionChange, sortDirection]);

    const { combinedDragAndSortProps, isDragActive } = useCombinedDragAndSort({
      canReorder,
      canSort,
      dragHandleProps,
      handleSortToggle,
      isDragging,
    });

    const renderLabel = () => {
      if (isLoading) {
        return <SkeletonLoader width={100} height={16} className="flex-1 bg-grey-60" />;
      }
      return (
        <>
          {label && (
            <GridCellText
              // Narrow columns clamp the label; the native tooltip recovers the full text on hover.
              title={label}
              className={cn(
                'text-xs font-medium text-left',
                hasIcon || open ? 'line-clamp-1 break-all' : 'break-normal',
                'group-hover:line-clamp-1 group-hover:break-all group-focus-within:line-clamp-1 group-focus-within:break-all',
              )}
            >
              {label}
            </GridCellText>
          )}
        </>
      );
    };

    // Render combined drag and sort area
    const renderCombinedDragAndSort = () => {
      const describedBy =
        [canSort && sortDir ? sortStateId : null, filter ? filterId : null].filter(Boolean).join(' ') || undefined;
      // Always use combined area
      if (canReorder) {
        return (
          <DragHandle
            variant={isDragging ? 'selected' : 'default'}
            className={cn(
              labelButtonVariants({ spacing: effectiveSpacing }),
              'flex flex-1 rounded-sm active:bg-blue-10 active:border active:border-blue-50',
              !onSortDirectionChange &&
                canSort &&
                'disabled:bg-transparent disabled:text-primary-dark disabled:hover:bg-grey-20',
              // Override cursor: pointer until drag starts, then grab/grabbing, unless sorting is disabled
              !isDragActive && !isDragging && canSort && 'cursor-pointer!',
              isDragActive && !isDragging && 'cursor-grab',
              isDragging && 'cursor-grabbing',
            )}
            aria-label={label || sortLabel || 'Drag handle'}
            aria-describedby={describedBy}
            {...combinedDragAndSortProps}
          >
            <div className="flex gap-0.5 w-full items-center shrink-0">
              {renderLabel()}
              {canSort &&
                (!sortDir && !filter ? (
                  <div className="gap-0.5 flex-1 justify-end items-center hidden group-hover:flex group-focus-within:flex">
                    <Icon icon="caretSort" size={14} />
                  </div>
                ) : (
                  <div className="flex gap-0.5 flex-1 justify-end items-center shrink-0">
                    {sortDir === 'asc' ? (
                      <Icon icon="arrowUp" size={14} />
                    ) : sortDir === 'desc' ? (
                      <Icon icon="arrowDown" size={14} />
                    ) : (
                      <Icon icon="caretSort" size={14} className="hidden group-hover:block group-focus-within:block" />
                    )}
                    {sortDir && (
                      <span id={sortStateId} className="sr-only">
                        {sortDir === 'asc' ? 'Sorted ascending' : 'Sorted descending'}
                      </span>
                    )}
                    {filter && (
                      <>
                        <SvgIcon icon="filter" size={12} />
                        <span id={filterId} className="sr-only">
                          Filtered
                        </span>
                      </>
                    )}
                  </div>
                ))}
            </div>
          </DragHandle>
        );
      }

      if (canSort) {
        // Named by the label alone so the icon labels and tooltip text stay out of the column's name.
        return (
          <button
            type="button"
            aria-label={label || sortLabel}
            aria-describedby={describedBy}
            data-testid={dataTestId ? `${dataTestId}-sort` : undefined}
            disabled={!onSortDirectionChange}
            className={cn(
              labelButtonVariants({ spacing: effectiveSpacing }),
              'flex flex-1 items-center rounded-sm py-1 text-primary-dark border border-transparent text-left focus-ring',
              !onSortDirectionChange && 'disabled:bg-transparent disabled:text-primary-dark disabled:hover:bg-grey-20',
              'px-2 cursor-pointer hover:bg-grey-20 active:bg-blue-10 active:border active:border-blue-50',
            )}
            {...combinedDragAndSortProps}
          >
            <span className="flex gap-0.5 w-full items-center shrink-0">
              {renderLabel()}
              {!sortDir && !filter ? (
                <span className="gap-0.5 flex-1 justify-end items-center hidden group-hover:flex group-focus-within:flex">
                  <Icon icon="caretSort" size={14} />
                </span>
              ) : (
                <span className="flex gap-0.5 flex-1 justify-end items-center shrink-0">
                  {sortDir === 'asc' ? (
                    <Icon icon="arrowUp" size={14} />
                  ) : sortDir === 'desc' ? (
                    <Icon icon="arrowDown" size={14} />
                  ) : (
                    <Icon icon="caretSort" size={14} className="hidden group-hover:block group-focus-within:block" />
                  )}
                  {sortDir && (
                    <span id={sortStateId} className="sr-only">
                      {sortDir === 'asc' ? 'Sorted ascending' : 'Sorted descending'}
                    </span>
                  )}
                  {filter && (
                    <>
                      <SvgIcon icon="filter" size={12} />
                      <span id={filterId} className="sr-only">
                        Filtered
                      </span>
                    </>
                  )}
                </span>
              )}
            </span>
          </button>
        );
      }

      return (
        <div
          className={cn(
            labelButtonVariants({ spacing: effectiveSpacing }),
            'flex flex-1 rounded-sm py-1 text-primary-dark border border-transparent',
          )}
        >
          <div className="flex gap-0.5 w-full items-center shrink-0">{renderLabel()}</div>
        </div>
      );
    };

    return (
      <Comp
        ref={composedRef}
        role="columnheader"
        data-slot="grid-head"
        data-spacing={effectiveSpacing}
        data-testid={dataTestId}
        className={cn(gridHeadVariants({ spacing: effectiveSpacing }), className, 'relative')}
        {...props}
        aria-sort={ariaSort}
      >
        <div
          className={cn(
            'absolute left-0 top-0',
            'h-full w-1 rounded-full transition-colors',
            showDropIndicator ? 'bg-blue-40' : 'bg-transparent',
            'z-10 pointer-events-none',
            // Apply translateX(-2px) for all except the first th in the row
            '[tr>th:not(:first-of-type)_&]:translate-x-[-2px]',
          )}
        />

        {renderCombinedDragAndSort()}
        <div className="flex items-center shrink-0 h-full">
          {/* Beside the drag handle, not inside it, so it is its own Tab stop and never sorts or drags. */}
          {tooltip && (
            <span data-slot="grid-head-tooltip" className="flex w-4 shrink-0 items-center justify-center text-grey-60">
              <Tooltip
                text={tooltip}
                side="top"
                className="max-w-72"
                aria-label={label ? `About ${label}` : 'About this column'}
              >
                <Icon icon={tooltipIcon} size={14} />
              </Tooltip>
            </span>
          )}
          {/* canSort alone does not show the menu */}
          {(canHide || canPin || canFilter || onMove || onResize) &&
            (menu ? (
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild ref={menuTriggerRef}>
                  <IconButton
                    icon="dotsVertical"
                    variant={open ? 'selected' : 'hollow'}
                    size="icon"
                    aria-label={menuLabel}
                    className={cn(
                      menuButtonVariants({ spacing: effectiveSpacing }),
                      hasIcon || open ? 'flex' : 'hidden group-hover:flex group-focus-within:flex',
                    )}
                  />
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  side="bottom"
                  sideOffset={3}
                  onCloseAutoFocus={(event) => {
                    // Focus already moved on, such as a click on another control.
                    const current = document.activeElement;
                    if (current && current !== document.body && current.isConnected) return;
                    event.preventDefault();
                    // The menu button shows only while its header has focus, so the header (or its first control) takes it first.
                    const head = headRef.current;
                    (head?.hasAttribute('tabindex')
                      ? head
                      : head?.querySelector<HTMLElement>('[tabindex], button')
                    )?.focus();
                    menuTriggerRef.current?.focus();
                  }}
                >
                  <ColumnMenu
                    canHide={canHide}
                    canPin={canPin}
                    canSort={canSort}
                    canFilter={canFilter}
                    showColumn={showColumn}
                    onShowColumnChange={onShowColumnChange}
                    pinPosition={pinPosition}
                    onPinPositionChange={onPinPositionChange}
                    sortDirection={sortDirection}
                    onSortDirectionChange={onSortDirectionChange}
                    columnLabel={label || sortLabel}
                    canMoveLeft={canMoveLeft}
                    canMoveRight={canMoveRight}
                    onMove={onMove}
                    canNarrow={canNarrow}
                    canWiden={canWiden}
                    canResetWidth={canResetWidth}
                    onResize={onResize}
                    closePopover={() => setOpen(false)}
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <Button
                variant="hollow"
                className={cn(
                  menuButtonVariants({ spacing: effectiveSpacing }),
                  'border border-transparent',
                  hasIcon ? 'flex' : 'hidden group-hover:flex group-focus-within:flex',
                  'bg-white text-primary-dark hover:bg-grey-20 hover:text-grey-50',
                )}
                aria-label={menuLabel}
                disabled
              >
                <Icon icon="dotsVertical" size={14} />
              </Button>
            ))}
          {canResize ? (
            // biome-ignore lint/a11y/noStaticElementInteractions: pointer-only drag handle; the column menu's Width row is the keyboard alternative
            // biome-ignore lint/a11y/noNoninteractiveElementInteractions: same drag handle, kept out of the tab order on purpose
            <div
              className={cn(
                'group/resize flex items-center justify-center shrink-0 h-full w-[7px]',
                header ? 'hover:cursor-col-resize' : '',
                isResizing ? 'cursor-col-resize' : '',
              )}
              onDoubleClick={header ? () => header.column.resetSize() : undefined}
              onMouseDown={header ? header.getResizeHandler() : undefined}
              onTouchStart={header ? header.getResizeHandler() : undefined}
            >
              <Separator
                orientation="vertical"
                className={cn(
                  header ? 'group-hover/resize:bg-blue-50 group-active/resize:bg-blue-50' : '',
                  isResizing ? 'bg-blue-50' : '',
                )}
              />
            </div>
          ) : !fixedPinning ? (
            <div className={cn('group/resize flex items-center justify-center shrink-0 h-full w-[7px]')}>
              <Separator orientation="vertical" />
            </div>
          ) : null}
        </div>
      </Comp>
    );
  },
);

GridHead.displayName = 'GridHead';

type GridHeadDragOverlayProps = React.ComponentProps<'div'> & {
  className?: string;
  spacing?: GridSpacing;
  label?: string;
  isLoading?: boolean;
  filter?: boolean;
};
function GridHeadDragOverlay({
  className,
  spacing,
  label,
  isLoading = false,
  filter = false,
  ...props
}: GridHeadDragOverlayProps) {
  const gridContext = useContext(GridContext);
  const effectiveSpacing = spacing || gridContext.spacing || 'cozy';

  return (
    <div
      data-spacing={effectiveSpacing}
      className={cn(gridHeadVariants({ spacing: effectiveSpacing }), 'border-none', className)}
      {...props}
    >
      <DragHandle
        className={cn(
          labelButtonVariants({ spacing: effectiveSpacing }),
          'w-full justify-between bg-blue-10! border border-blue-50 m-3',
        )}
        aria-label={label || 'Drag handle'}
      >
        {label && <GridCellText className="text-xs font-medium break-normal text-left">{label}</GridCellText>}
        {filter && (
          <div className="flex gap-0.5 items-center shrink-0">
            <SvgIcon icon="filter" size={12} ariaLabel="Filtered" />
          </div>
        )}
      </DragHandle>
    </div>
  );
}

export { GridHead, GridHeadDragOverlay, type GridHeadProps, type GridHeadTooltipIcon };
